import React, { useContext, useEffect, useReducer, useState } from 'react';
import axios from 'axios';
import { Store } from '../Store';
import { getError } from '../utils';
import LoadingBox from '../components/LoadingBox';
import MessageBox from '../components/MessageBox';
import Container from 'react-bootstrap/Container';
import Button from 'react-bootstrap/Button';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Table from 'react-bootstrap/Table';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';
import { toast } from 'react-toastify';
import { Helmet } from 'react-helmet-async';

const reducer = (state, action) => {
    switch (action.type) {
        case 'FETCH_REQUEST':
            return { ...state, loading: true };
        case 'FETCH_SUCCESS':
            return { ...state, workers: action.payload, loading: false };
        case 'FETCH_FAIL':
            return { ...state, loading: false, error: action.payload };
        case 'CREATE_REQUEST':
            return { ...state, loadingCreate: true };
        case 'CREATE_SUCCESS':
            return { ...state, loadingCreate: false, successCreate: true };
        case 'CREATE_FAIL':
            return { ...state, loadingCreate: false };
        case 'CREATE_RESET':
            return { ...state, successCreate: false };
        case 'DELETE_REQUEST':
            return { ...state, loadingDelete: true, successDelete: false };
        case 'DELETE_SUCCESS':
            return { ...state, loadingDelete: false, successDelete: true };
        case 'DELETE_FAIL':
            return { ...state, loadingDelete: false };
        case 'DELETE_RESET':
            return { ...state, successDelete: false };
        case 'WAITER_REGISTER_REQUEST':
            return { ...state, loadingWaiter: true };
        case 'WAITER_REGISTER_SUCCESS':
            return { ...state, loadingWaiter: false, successCreate: true };
        case 'WAITER_REGISTER_FAIL':
            return { ...state, loadingWaiter: false };
        default:
            return state;
    }
};

export default function WorkerScreen() {
    const [{ loading, error, workers, loadingCreate, successCreate, successDelete, loadingWaiter }, dispatch] = useReducer(reducer, {
        loading: true,
        error: '',
        workers: [],
    });

    const { state } = useContext(Store);
    const { userInfo } = state;

    const [showModal, setShowModal] = useState(false);
    const [showWaiterModal, setShowWaiterModal] = useState(false);
    const [showPayModal, setShowPayModal] = useState(false);
    const [showDailyModal, setShowDailyModal] = useState(false);

    const [waiterName, setWaiterName] = useState('');
    const [waiterEmail, setWaiterEmail] = useState('');
    const [waiterPassword, setWaiterPassword] = useState('');
    const [name, setName] = useState('');
    const [role, setRole] = useState('');
    const [salary, setSalary] = useState(0);
    const [paymentType, setPaymentType] = useState('Monthly');
    const [dailyRate, setDailyRate] = useState(0);
    const [isEdit, setIsEdit] = useState(false);
    const [editId, setEditId] = useState('');

    const [payAmount, setPayAmount] = useState(0);
    const [payMonth, setPayMonth] = useState(new Date().toISOString().substring(0, 7)); // YYYY-MM
    const [selectedWorker, setSelectedWorker] = useState(null);

    const [dailyDate, setDailyDate] = useState(new Date().toISOString().substring(0, 10));
    const [dailyAmount, setDailyAmount] = useState(0);

    useEffect(() => {
        const fetchData = async () => {
            try {
                dispatch({ type: 'FETCH_REQUEST' });
                const { data } = await axios.get('/api/workers', {
                    headers: { Authorization: `Bearer ${userInfo.token}` },
                });
                dispatch({ type: 'FETCH_SUCCESS', payload: data });
            } catch (err) {
                dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
            }
        };
        if (successCreate || successDelete) {
            dispatch({ type: successCreate ? 'CREATE_RESET' : 'DELETE_RESET' });
            fetchData();
        } else {
            fetchData();
        }
    }, [userInfo, successCreate, successDelete]);

    const submitHandler = async (e) => {
        e.preventDefault();
        try {
            dispatch({ type: 'CREATE_REQUEST' });
            if (isEdit) {
                await axios.put(
                    `/api/workers/${editId}`,
                    { name, role, salary, paymentType, dailyRate },
                    { headers: { Authorization: `Bearer ${userInfo.token}` } }
                );
                toast.success('Worker updated successfully');
            } else {
                await axios.post(
                    '/api/workers',
                    { name, role, salary, paymentType, dailyRate },
                    { headers: { Authorization: `Bearer ${userInfo.token}` } }
                );
                toast.success('Worker added successfully');
            }
            dispatch({ type: 'CREATE_SUCCESS' });
            setShowModal(false);
            resetForm();
        } catch (err) {
            toast.error(getError(err));
            dispatch({ type: 'CREATE_FAIL' });
        }
    };

    const registerWaiterHandler = async (e) => {
        e.preventDefault();
        if (!waiterName || !waiterEmail || !waiterPassword) {
            toast.error('Please fill all fields');
            return;
        }

        try {
            dispatch({ type: 'WAITER_REGISTER_REQUEST' });
            await axios.post(
                '/api/users/register-waiter',
                { name: waiterName, email: waiterEmail, password: waiterPassword },
                { headers: { Authorization: `Bearer ${userInfo.token}` } }
            );
            toast.success('Waiter registered successfully as user and worker');
            dispatch({ type: 'WAITER_REGISTER_SUCCESS' });
            setWaiterName('');
            setWaiterEmail('');
            setWaiterPassword('');
            setShowWaiterModal(false);
        } catch (err) {
            toast.error(getError(err));
            dispatch({ type: 'WAITER_REGISTER_FAIL' });
        }
    };

    const paySalaryHandler = async (e) => {
        e.preventDefault();
        try {
            dispatch({ type: 'CREATE_REQUEST' });
            await axios.post(
                `/api/workers/${selectedWorker._id}/pay`,
                { month: payMonth, amount: payAmount },
                { headers: { Authorization: `Bearer ${userInfo.token}` } }
            );
            toast.success(`Salary paid to ${selectedWorker.name}`);
            dispatch({ type: 'CREATE_SUCCESS' });
            setShowPayModal(false);
        } catch (err) {
            toast.error(getError(err));
            dispatch({ type: 'CREATE_FAIL' });
        }
    };

    const dailyRecordHandler = async (e) => {
        e.preventDefault();
        try {
            dispatch({ type: 'CREATE_REQUEST' });
            await axios.post(
                `/api/workers/${selectedWorker._id}/daily-record`,
                { date: dailyDate, amountGained: dailyAmount },
                { headers: { Authorization: `Bearer ${userInfo.token}` } }
            );
            toast.success(`Daily record saved for ${selectedWorker.name}`);
            dispatch({ type: 'CREATE_SUCCESS' });
            setShowDailyModal(false);
        } catch (err) {
            toast.error(getError(err));
            dispatch({ type: 'CREATE_FAIL' });
        }
    };

    const deleteHandler = async (worker) => {
        if (window.confirm('Are you sure to delete?')) {
            try {
                dispatch({ type: 'DELETE_REQUEST' });
                await axios.delete(`/api/workers/${worker._id}`, {
                    headers: { Authorization: `Bearer ${userInfo.token}` },
                });
                toast.success('Worker deleted successfully');
                dispatch({ type: 'DELETE_SUCCESS' });
            } catch (err) {
                toast.error(getError(err));
                dispatch({ type: 'DELETE_FAIL' });
            }
        }
    };

    const resetForm = () => {
        setName('');
        setRole('');
        setSalary(0);
        setPaymentType('Monthly');
        setDailyRate(0);
        setIsEdit(false);
        setEditId('');
    };

    const editHandler = (worker) => {
        setName(worker.name);
        setRole(worker.role);
        setSalary(worker.salary);
        setPaymentType(worker.paymentType || 'Monthly');
        setDailyRate(worker.dailyRate || 0);
        setIsEdit(true);
        setEditId(worker._id);
        setShowModal(true);
    };

    const openPayModal = async (worker) => {
        setSelectedWorker(worker);
        let initialAmount = worker.salary;

        if (worker.paymentType === 'Daily') {
            try {
                const { data } = await axios.get(`/api/workers/${worker._id}/daily-records?unpaidOnly=true`, {
                    headers: { Authorization: `Bearer ${userInfo.token}` },
                });
                initialAmount = data.reduce((acc, rec) => acc + rec.amountGained, 0);
            } catch (err) {
                console.error("Error fetching unpaid records", err);
            }
        }

        setPayAmount(initialAmount);
        setShowPayModal(true);
    };

    const openDailyModal = (worker) => {
        setSelectedWorker(worker);
        setDailyAmount(worker.dailyRate || 0);
        setDailyDate(new Date().toISOString().substring(0, 10));
        setShowDailyModal(true);
    };

    return (
        <Container>
            <Helmet>
                <title>Worker Management</title>
            </Helmet>
            <Row className="my-3">
                <Col>
                    <h1>Worker Management</h1>
                </Col>
                <Col className="text-end">
                    <Button variant="outline-primary" className="me-2" onClick={() => setShowWaiterModal(true)}>
                        <i className="fas fa-user-plus"></i> Register Waiter
                    </Button>
                    <Button variant="primary" onClick={() => { resetForm(); setShowModal(true); }}>
                        Add Worker
                    </Button>
                </Col>
            </Row>

            {loading ? (
                <LoadingBox />
            ) : error ? (
                <MessageBox variant="danger">{error}</MessageBox>
            ) : (
                <Table responsive striped bordered hover>
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Role</th>
                            <th>Pay Type</th>
                            <th>Rate/Salary</th>
                            <th>Registered By</th>
                            <th>Last Paid</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {workers.map((worker) => (
                            <tr key={worker._id}>
                                <td>{worker.name}</td>
                                <td>{worker.role}</td>
                                <td>{worker.paymentType || 'Monthly'}</td>
                                <td>
                                    {worker.paymentType === 'Daily'
                                        ? `${worker.dailyRate}/day`
                                        : `${worker.salary}/month`}
                                </td>
                                <td>
                                    {worker.managerId ? worker.managerId.name : 'System'}
                                </td>
                                <td>
                                    {worker.paymentHistory.length > 0
                                        ? worker.paymentHistory[worker.paymentHistory.length - 1].month
                                        : 'Never'}
                                </td>
                                <td>
                                    <Button variant="light" size="sm" onClick={() => editHandler(worker)}>
                                        Edit
                                    </Button>
                                    {worker.paymentType === 'Daily' && (
                                        <Button variant="info" size="sm" className="ms-2" onClick={() => openDailyModal(worker)}>
                                            Daily Record
                                        </Button>
                                    )}
                                    <Button variant="success" size="sm" className="ms-2" onClick={() => openPayModal(worker)}>
                                        Pay Salary
                                    </Button>
                                    <Button variant="danger" size="sm" className="ms-2" onClick={() => deleteHandler(worker)}>
                                        Delete
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}

            {/* Add/Edit Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{isEdit ? 'Edit Worker' : 'Add New Worker'}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={submitHandler}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Name</Form.Label>
                            <Form.Control value={name} onChange={(e) => setName(e.target.value)} required />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Role</Form.Label>
                            <Form.Control value={role} onChange={(e) => setRole(e.target.value)} required />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Payment Type</Form.Label>
                            <Form.Select value={paymentType} onChange={(e) => setPaymentType(e.target.value)}>
                                <option value="Monthly">Monthly</option>
                                <option value="Daily">Daily</option>
                            </Form.Select>
                        </Form.Group>
                        {paymentType === 'Monthly' ? (
                            <Form.Group className="mb-3">
                                <Form.Label>Monthly Salary</Form.Label>
                                <Form.Control type="number" value={salary} onChange={(e) => setSalary(Number(e.target.value))} required />
                            </Form.Group>
                        ) : (
                            <Form.Group className="mb-3">
                                <Form.Label>Daily Rate</Form.Label>
                                <Form.Control type="number" value={dailyRate} onChange={(e) => setDailyRate(Number(e.target.value))} required />
                            </Form.Group>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>Close</Button>
                        <Button variant="primary" type="submit" disabled={loadingCreate}>
                            {loadingCreate ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Daily Record Modal */}
            <Modal show={showDailyModal} onHide={() => setShowDailyModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Daily Record: {selectedWorker?.name}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={dailyRecordHandler}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Date</Form.Label>
                            <Form.Control type="date" value={dailyDate} onChange={(e) => setDailyDate(e.target.value)} required />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Amount Earned</Form.Label>
                            <Form.Control type="number" value={dailyAmount} onChange={(e) => setDailyAmount(Number(e.target.value))} required />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowDailyModal(false)}>Close</Button>
                        <Button variant="primary" type="submit" disabled={loadingCreate}>
                            {loadingCreate ? 'Saving...' : 'Save Record'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Payment Modal */}
            <Modal show={showPayModal} onHide={() => setShowPayModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Pay Salary: {selectedWorker?.name}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={paySalaryHandler}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Month</Form.Label>
                            <Form.Control type="month" value={payMonth} onChange={(e) => setPayMonth(e.target.value)} required />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Amount to Pay</Form.Label>
                            <Form.Control type="number" value={payAmount} onChange={(e) => setPayAmount(Number(e.target.value))} required />
                            {selectedWorker?.paymentType === 'Daily' && (
                                <Form.Text className="text-muted">
                                    This defaults to the sum of all unpaid daily records.
                                </Form.Text>
                            )}
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowPayModal(false)}>Close</Button>
                        <Button variant="success" type="submit" disabled={loadingCreate}>
                            {loadingCreate ? 'Processing...' : 'Confirm Payment'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Register Waiter Modal */}
            <Modal show={showWaiterModal} onHide={() => setShowWaiterModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Register New Waiter</Modal.Title>
                </Modal.Header>
                <Form onSubmit={registerWaiterHandler}>
                    <Modal.Body>
                        <p className="text-muted small">
                            This will create a system user account for the waiter and add them to your worker list automatically.
                        </p>
                        <Form.Group className="mb-3">
                            <Form.Label>Full Name</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder="Enter full name"
                                value={waiterName}
                                onChange={(e) => setWaiterName(e.target.value)}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Email Address</Form.Label>
                            <Form.Control
                                type="email"
                                placeholder="Enter email"
                                value={waiterEmail}
                                onChange={(e) => setWaiterEmail(e.target.value)}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Password</Form.Label>
                            <Form.Control
                                type="password"
                                placeholder="Create password"
                                value={waiterPassword}
                                onChange={(e) => setWaiterPassword(e.target.value)}
                                required
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => setShowWaiterModal(false)}>Cancel</Button>
                        <Button variant="primary" type="submit" disabled={loadingWaiter}>
                            {loadingWaiter ? 'Registering...' : 'Register Waiter'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </Container>
    );
}
